import React, { Component } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { HTTPService } from "./../../services/httpService";
import CameraCapture from './../common/CameraCapture';

const API = new HTTPService();

class CompletionPhotosModal extends Component {
  constructor(props) {
    super(props);

    // Detect if mobile device
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    this.state = {
      isMobile: isMobile,

      // Completion photos (1-4)
      completionPhotos: [],

      // Camera state
      showCamera: false,

      // UI state
      loading: false,
      error: null,
      uploadProgress: 0
    };

    this.handlePhotoCapture = this.handlePhotoCapture.bind(this);
    this.handleFileUpload = this.handleFileUpload.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  // ========================================================================
  // PHOTO HANDLERS
  // ========================================================================

  async handlePhotoCapture(photoData) {
    console.log("📸 Completion photo captured");

    // Convert data URL to blob for compression
    const blob = await (await fetch(photoData.preview)).blob();
    const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });

    // Compress the captured photo
    const compressed = await this.compressImage(file);

    const updatedPhotos = [...this.state.completionPhotos, {
      blob: compressed.blob,
      preview: compressed.preview
    }];

    this.setState({
      completionPhotos: updatedPhotos,
      showCamera: false
    });
  }

  openCamera = () => {
    if (this.state.completionPhotos.length >= 4) {
      alert('Maximum 4 completion photos allowed');
      return;
    }

    if (this.state.isMobile) {
      this.setState({
        showCamera: true
      });
    }
  };

  removePhoto = (index) => {
    const updatedPhotos = this.state.completionPhotos.filter((_, i) => i !== index);
    this.setState({ completionPhotos: updatedPhotos });
  };

  // ========================================================================
  // IMAGE COMPRESSION
  // ========================================================================

  compressImage(file, maxWidth = 1920, maxHeight = 1080, quality = 0.8) {
    return new Promise((resolve) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const img = new Image();

        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions while maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to blob
          canvas.toBlob(
            (blob) => {
              resolve({
                blob: blob,
                preview: canvas.toDataURL('image/jpeg', quality)
              });
            },
            'image/jpeg',
            quality
          );
        };

        img.src = e.target.result;
      };

      reader.readAsDataURL(file);
    });
  }

  // ========================================================================
  // FILE UPLOAD HANDLER (Desktop)
  // ========================================================================

  async handleFileUpload(event) {
    const files = event.target.files;

    if (!files || files.length === 0) return;

    for (const file of Array.from(files)) {
      if (this.state.completionPhotos.length >= 4) {
        alert('Maximum 4 completion photos allowed');
        return;
      }

      // Compress the image
      const compressed = await this.compressImage(file);

      this.setState(prevState => ({
        completionPhotos: [...prevState.completionPhotos, {
          blob: compressed.blob,
          preview: compressed.preview
        }]
      }));
    }
  }

  // ========================================================================
  // UPLOAD TO SERVER
  // ========================================================================

  async handleSubmit() {
    try {
      // Validation
      if (this.state.completionPhotos.length === 0) {
        this.setState({ error: 'At least 1 completion photo is required' });
        return;
      }

      this.setState({ loading: true, error: null, uploadProgress: 10 });

      console.log("📤 Uploading completion photos to server");

      // Prepare form data
      const formData = new FormData();

      // Add completion photos (already compressed)
      for (let i = 0; i < this.state.completionPhotos.length; i++) {
        const photo = this.state.completionPhotos[i];

        // All photos now have a blob property (compressed)
        formData.append('completion_photos', photo.blob, `completion_${i + 1}_${Date.now()}.jpg`);
      }

      this.setState({ uploadProgress: 50 });

      // Upload to server
      const { requestId } = this.props;
      const response = await API.post(`/requests/${requestId}/completion/photos`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      this.setState({ uploadProgress: 100 });

      console.log("✅ Completion photos uploaded successfully", response.data);

      // Close modal and notify parent
      setTimeout(() => {
        this.props.onSuccess();
        this.props.onHide();
        this.setState({
          completionPhotos: [],
          loading: false,
          uploadProgress: 0
        });
      }, 500);

    } catch (error) {
      console.error("❌ Upload error:", error);
      this.setState({
        error: 'Failed to upload photos. Please try again.',
        loading: false,
        uploadProgress: 0
      });
    }
  }

  handleClose = () => {
    if (!this.state.loading) {
      this.setState({
        completionPhotos: [],
        error: null,
        uploadProgress: 0
      });
      this.props.onHide();
    }
  };

  // ========================================================================
  // RENDER
  // ========================================================================

  render() {
    const { show } = this.props;
    const { isMobile, completionPhotos, showCamera, loading, error, uploadProgress } = this.state;

    // Camera Modal (Mobile)
    if (showCamera) {
      return (
        <CameraCapture
          onCapture={this.handlePhotoCapture}
          onClose={() => this.setState({ showCamera: false })}
        />
      );
    }

    return (
      <Modal
        show={show}
        onHide={this.handleClose}
        size="lg"
        backdrop={loading ? 'static' : true}
        keyboard={!loading}
      >
        <Modal.Header closeButton={!loading}>
          <Modal.Title>Upload Completion Photos</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {/* Instructions */}
          <div className="alert alert-info mb-3">
            <strong>Upload 1-4 photos</strong> showing the completed work
          </div>

          {/* Photo Upload Section */}
          <div className="mb-4">
            <h6>Completion Photos ({completionPhotos.length}/4)</h6>

            {/* Photo Previews */}
            <div className="row mb-3">
              {completionPhotos.map((photo, index) => (
                <div key={index} className="col-6 col-md-3 mb-2">
                  <div className="position-relative">
                    <img
                      src={photo.preview}
                      alt={`Completion ${index + 1}`}
                      className="img-thumbnail w-100"
                      style={{ height: '150px', objectFit: 'cover' }}
                    />
                    <button
                      type="button"
                      className="btn btn-danger btn-sm position-absolute"
                      style={{ top: '5px', right: '5px' }}
                      onClick={() => this.removePhoto(index)}
                      disabled={loading}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Add Photo Buttons */}
            {completionPhotos.length < 4 && (
              <div className="d-flex gap-2">
                {isMobile ? (
                  <Button
                    variant="primary"
                    onClick={this.openCamera}
                    disabled={loading}
                  >
                    📸 Take Photo
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    onClick={() => document.getElementById('completion-file-input').click()}
                    disabled={loading}
                  >
                    📁 Upload Photo
                  </Button>
                )}
                <input
                  id="completion-file-input"
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={this.handleFileUpload}
                  disabled={loading}
                />
              </div>
            )}
          </div>

          {/* Upload Progress */}
          {loading && (
            <div className="mb-3">
              <div className="progress">
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  style={{ width: `${uploadProgress}%` }}
                  aria-valuenow={uploadProgress}
                  aria-valuemin="0"
                  aria-valuemax="100"
                >
                  {uploadProgress}%
                </div>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={this.handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={this.handleSubmit}
            disabled={loading || completionPhotos.length === 0}
          >
            {loading ? 'Uploading...' : 'Upload Photos'}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default CompletionPhotosModal;
